import { COLORS } from '@bcm/constants';
import { isWeb } from '../../../../src/platformUtils';
import { DateTime } from 'luxon';

const black = COLORS.black;
const white = COLORS.white;

export const BURN_BSA_TYPES = {
	burnSuperficial: {
		code: 'burnSuperficial',
		display: 'Superficial',
		color: '#f5a623',
		textColor: black,
	},
	burnPartialThickness: {
		code: 'burnPartialThickness',
		display: 'Partial 2nd',
		color: '#db3e01',
		textColor: black,
	},
	burnDeepPartialThickness: {
		code: 'burnDeepPartialThickness',
		display: 'Deep 2nd',
		color: '#b00101',
		textColor: white,
	},
	burnFullThickness: {
		code: 'burnFullThickness',
		display: 'Full 3rd',
		color: '#5d0120',
		textColor: white,
	},
	amputation: {
		code: 'amputation',
		display: 'Amputation',
		color: '#010101',
		textColor: white,
	},
	donor: {
		code: 'donor',
		display: 'Donor',
		color: '#95ea99',
		textColor: black,
	},
	autograft: {
		code: 'autograft',
		display: 'Autograft',
		color: '#55c9dd',
		textColor: black,
	},
	allograft: {
		code: 'allograft',
		display: 'Allograft',
		color: '#2041a3',
		textColor: white,
	},
	skinSubstitute: {
		code: 'skinSubstitute',
		display: 'Substitute',
		color: '#514CF1',
		textColor: white,
	},
	cfu:  {
		code: 'cfu',
		display: 'CFU',
		color: '#a3209e',
		textColor: white,
	},
};

const colorClustering = {
	rgb0: 'none',
	rgb16387064: 'none',
	rgb16581375: 'none'
};
const COLOR_RGB_VALUES = [`rgb${255*3}`, 'rgb0'];
const COLOR_NAME_MAP = {};
Object.keys(BURN_BSA_TYPES).map((key) => {
	const hexColor = BURN_BSA_TYPES[key].color;
	const r0 = parseInt(hexColor.substring(1,3), 16);
	const g0 = parseInt(hexColor.substring(3,5), 16);
	const b0 = parseInt(hexColor.substring(5,7), 16);
	const rgb0 = `${r0}${g0}${b0}`;
	const bound = 2;
	for (let r = Math.max(r0 - bound, 1); r < Math.min(r0 + bound, 255); r++) {
		for (let g = Math.max(g0 - bound, 1); g < Math.min(g0 + bound, 255); g++) {
			for (let b = Math.max(b0 - bound, 1); b < Math.min(b0 + bound, 255); b++) {
				const key = `rgb${r * g * b}`;
				colorClustering[key] = `rgb${rgb0}`;
			}
		}
	}

	COLOR_RGB_VALUES.push(`rgb${rgb0}`);
	COLOR_NAME_MAP[`rgb${rgb0}`] = BURN_BSA_TYPES[key].code;
});

const lundBrowderMatrix = {
	//				0		1		5		10		15		18
	head_F_M:		[9.5,	8.5,	6.5,	5.5,	4.5].map(n => n / 100),
	head_B_M:		[9.5,	8.5,	6.5,	5.5,	4.5].map(n => n / 100),
	neck_F_M:		[1,		1,		1,		1,		1].map(n => n / 100),
	neck_B_M:		[1,		1,		1,		1,		1].map(n => n / 100),
	torso_F_M:		[13,	13,		13,		13,		13].map(n => n / 100),
	torso_B_M:		[13,	13,		13,		13,		13].map(n => n / 100),
	genitalia_F_M:	[1,		1,		1,		1,		1].map(n => n / 100),
	buttock_B_L:	[2.5,	2.5,	2.5,	2.5,	2.5].map(n => n / 100),
	buttock_B_R:	[2.5,	2.5,	2.5,	2.5,	2.5].map(n => n / 100),
	armUpper_F_L:	[2,		2,		2,		2,		2].map(n => n / 100),
	armUpper_F_R:	[2,		2,		2,		2,		2].map(n => n / 100),
	armUpper_B_L:	[2,		2,		2,		2,		2].map(n => n / 100),
	armUpper_B_R:	[2,		2,		2,		2,		2].map(n => n / 100),
	armLower_F_L:	[1.5,	1.5,	1.5,	1.5,	1.5].map(n => n / 100),
	armLower_F_R:	[1.5,	1.5,	1.5,	1.5,	1.5].map(n => n / 100),
	armLower_B_L:	[1.5,	1.5,	1.5,	1.5,	1.5].map(n => n / 100),
	armLower_B_R:	[1.5,	1.5,	1.5,	1.5,	1.5].map(n => n / 100),
	thigh_F_L:		[2.75,	3.25,	4,		4.25,	4.5].map(n => n / 100),
	thigh_F_R:		[2.75,	3.25,	4,		4.25,	4.5].map(n => n / 100),
	thigh_B_L:		[2.75,	3.25,	4,		4.25,	4.5].map(n => n / 100),
	thigh_B_R:		[2.75,	3.25,	4,		4.25,	4.5].map(n => n / 100),
	hand_F_L:		[1.25,	1.25,	1.25,	1.25,	1.25].map(n => n / 100),
	hand_F_R:		[1.25,	1.25,	1.25,	1.25,	1.25].map(n => n / 100),
	hand_B_L:		[1.25,	1.25,	1.25,	1.25,	1.25].map(n => n / 100),
	hand_B_R:		[1.25,	1.25,	1.25,	1.25,	1.25].map(n => n / 100),
	calf_F_L:		[2.5,	2.5,	2.5,	3,		3.25].map(n => n / 100),
	calf_F_R:		[2.5,	2.5,	2.5,	3,		3.25].map(n => n / 100),
	calf_B_L:		[2.5,	2.5,	2.5,	3,		3.25].map(n => n / 100),
	calf_B_R:		[2.5,	2.5,	2.5,	3,		3.25].map(n => n / 100),
	foot_F_L:		[1.75,	1.75,	1.75,	1.75,	1.75].map(n => n / 100),
	foot_F_R:		[1.75,	1.75,	1.75,	1.75,	1.75].map(n => n / 100),
	foot_B_L:		[1.75,	1.75,	1.75,	1.75,	1.75].map(n => n / 100),
	foot_B_R:		[1.75,	1.75,	1.75,	1.75,	1.75].map(n => n / 100),
};

export const lundBrowderDisplayNames = {
	head_F_M: 'Head, Face',
	head_B_M: 'Head, Back',
	neck_F_M: 'Neck, Front',
	neck_B_M: 'Neck, Back',
	torso_F_M: 'Torso, Front',
	torso_B_M: 'Torso, Back',
	armUpper_F_L: 'Upper Arm, Front Left',
	armUpper_F_R: 'Upper Arm, Front Right',
	armUpper_B_L: 'Upper Arm, Back Left',
	armUpper_B_R: 'Upper Arm, Back Right',
	genitalia_F_M: 'Genitalia',
	buttock_B_L: 'Buttock, Left',
	buttock_B_R: 'Buttock, Right',
	armLower_F_L: 'Lower Arm, Front Left',
	armLower_F_R: 'Lower Arm, Front Right',
	armLower_B_L: 'Lower Arm, Back Left',
	armLower_B_R: 'Lower Arm, Back Right',
	thigh_F_L: 'Thigh, Front Left',
	thigh_F_R: 'Thigh, Front Right',
	thigh_B_L: 'Thigh, Back Left',
	thigh_B_R: 'Thigh, Back Right',
	hand_F_L: 'Hand, Palm Left',
	hand_F_R: 'Hand, Palm Right',
	hand_B_L: 'Hand, Back Left',
	hand_B_R: 'Hand, Back Right',
	calf_F_L: 'Calf, Front Left',
	calf_F_R: 'Calf, Front Right',
	calf_B_L: 'Calf, Back Left',
	calf_B_R: 'Calf, Back Right',
	foot_F_L: 'Foot, Top Left',
	foot_F_R: 'Foot, Top Right',
	foot_B_L: 'Foot, Bottom Left',
	foot_B_R: 'Foot, Bottom Right',
};

export const criticalBodySites = [
	'head_F_M',
	'hands',
	'foot_F_L',
	'foot_F_R',
	'foot_B_L',
	'foot_B_R',
	'hand_F_L',
	'hand_F_R',
	'hand_B_L',
	'hand_B_R',
	'genitalia_F_M',
];

const alphaMap = {
	alpha255_F_L:	'head_F_M',
	alpha255_F_R:	'head_F_M',
	alpha255_B_L:	'head_B_M',
	alpha255_B_R:	'head_B_M',
	alpha254_F_L:	'neck_F_M',
	alpha254_F_R:	'neck_F_M',
	alpha254_B_L:	'neck_B_M',
	alpha254_B_R:	'neck_B_M',
	alpha253_F_L:	'torso_F_M',
	alpha253_F_R:	'torso_F_M',
	alpha253_B_L:	'torso_B_M',
	alpha253_B_R:	'torso_B_M',
	alpha252_F_L:	'armUpper_F_L',
	alpha252_F_R:	'armUpper_F_R',
	alpha252_B_L:	'armUpper_B_L',
	alpha252_B_R:	'armUpper_B_R',
	alpha251_F_L:	'genitalia_F_M',
	alpha251_F_R:	'genitalia_F_M',
	alpha251_B_L:	'buttock_B_L',
	alpha251_B_R:	'buttock_B_R',
	alpha250_F_L:	'armLower_F_L',
	alpha250_F_R:	'armLower_F_R',
	alpha250_B_L:	'armLower_B_L',
	alpha250_B_R:	'armLower_B_R',
	alpha249_F_L:	'thigh_F_L',
	alpha249_F_R:	'thigh_F_R',
	alpha249_B_L:	'thigh_B_L',
	alpha249_B_R:	'thigh_B_R',
	alpha248_F_L:	'hand_F_L',
	alpha248_F_R:	'hand_F_R',
	alpha248_B_L:	'hand_B_L',
	alpha248_B_R:	'hand_B_R',
	alpha247_F_L:	'calf_F_L',
	alpha247_F_R:	'calf_F_R',
	alpha247_B_L:	'calf_B_L',
	alpha247_B_R:	'calf_B_R',
	alpha246_F_L:	'foot_F_L',
	alpha246_F_R:	'foot_F_R',
	alpha246_B_L:	'foot_B_L',
	alpha246_B_R:	'foot_B_R',
	alpha0_F_L:		'none',
	alpha0_F_R:		'none',
	alpha0_B_L:		'none',
	alpha0_B_R:		'none',
};

export const calculateTBSA = (data, dims, birthdate, isFront) => {

	const now = DateTime.now();
	const age = now.diff(birthdate, 'years').years;

	const ageIndex = (age < 1 ? 0
		: age < 5 ? 1
			: age < 10 ? 2
				: age < 15 ? 3
					: 4 );

	const processedData = {};
	Object.keys(alphaMap).map((key) => {
		const bodySite = alphaMap[key];
		processedData[bodySite] = {
			total: 0,
		};
	});

	const FB = isFront ? 'F' : 'B';
	let lastRgbMap = 'none';
	const surroundingThreshold = isWeb ? 2 : 1;
	for (let i = 0; i < data.length; i += 4) {
		const a = data[i+3];
		if(a === 0) continue;
		const side = ((i / 4) % dims.width ) < (dims.width / 2) ? 'L' : 'R';
		const rgbProd = data[i] * data[i+1] * data[i+2];
		const rgbSumName = `rgb${rgbProd}`;
		let rgbMap = colorClustering[rgbSumName] ? colorClustering[rgbSumName] : lastRgbMap;

		const rgbName = COLOR_NAME_MAP[rgbMap];
		const alphaValue = `alpha${a}_${FB}_${side}`;
		const bodySite = alphaMap[alphaValue];

		// Check surrounding 4 pixels for matching alpha value
		const la = (data[i-1] && data[i-1] === a) ? 1 : 0;
		const ra = (data[i+7] && data[i+7] === a) ? 1 : 0;
		const ua = (data[i + dims.width * 4 + 3] && data[i + dims.width * 4 + 3] === a) ? 1 : 0;
		const da = (data[i - dims.width * 4 + 3] && data[i - dims.width * 4 + 3] === a) ? 1 : 0;

		const sum = la + ra + ua + da;

		if(sum > surroundingThreshold && processedData[bodySite]){
			processedData[bodySite].total += 1;
			processedData[bodySite][rgbName] = (processedData[bodySite][rgbName]) ? processedData[bodySite][rgbName] + 1 : 1;
		}
		lastRgbMap = rgbMap;
	}

	const tbsaRes = {};
	Object.keys(BURN_BSA_TYPES).map((severity) => {
		tbsaRes[severity] = 0;
	});

	const bodySiteRes = {};
	const sideChar = isFront ? 'F' : 'B';
	Object.keys(lundBrowderMatrix).map((bodySite) => {
		if (bodySite.split('_')[1] === sideChar) {
			bodySiteRes[bodySite] = {};
			Object.keys(BURN_BSA_TYPES).map((severity) => {
				bodySiteRes[bodySite][severity] = {};
			});
		}
	});
	for (const bodySite in processedData) {
		if(bodySite !== 'none') {
			const item = processedData[bodySite];
			for (const severity in item) {
				if(severity && severity !== 'undefined' && severity !== 'total') {
					const percent = item[severity] / item.total;
					const maxBsa = lundBrowderMatrix[bodySite][ageIndex];
					const tbsa = percent * maxBsa;
					tbsaRes[severity] += tbsa;
					bodySiteRes[bodySite][severity].percent = percent;
					bodySiteRes[bodySite].isAffected = true;
				}
			}
		}
	}

	// Correct for TBSA based on amputations
	const sideTBSA = isFront ? .48 : .52;
	const amputationCorrection = sideTBSA / (sideTBSA - tbsaRes.amputation);
	const correctedTBSA = {};
	const correctedBodySite = { ...bodySiteRes};
	Object.keys(tbsaRes).map((severity) => {
		correctedTBSA[severity] = tbsaRes[severity] * amputationCorrection;
	});
	correctedTBSA.amputation = tbsaRes.amputation;
	// Correct bodySite res based on amputations
	Object.keys(bodySiteRes).map((bodySiteKey) => {
		let bodySiteTBSA;
		if(!bodySiteRes[bodySiteKey].amputation.percent) {
			bodySiteTBSA = lundBrowderMatrix[bodySiteKey][ageIndex] * amputationCorrection;
		}
		else {
			const bsaAmputationCorrection = (1 - bodySiteRes[bodySiteKey].amputation.percent > 0.01) ? 1 - bodySiteRes[bodySiteKey].amputation.percent : 0;
			bodySiteTBSA = lundBrowderMatrix[bodySiteKey][ageIndex] * amputationCorrection * bsaAmputationCorrection;
		}

		correctedBodySite[bodySiteKey].bodySiteTBSA = bodySiteTBSA;
	});

	return {
		tbsa: correctedTBSA,
		bodySites: correctedBodySite
	};
};